import cl from 'clsx'
import { useCallback, useRef, useState, useEffect } from 'react'
import { z } from 'zod'

import { useOptimisticValue } from '@/shared/hooks'
import { DisableTooltip, GreyRubleIconInline } from '@/shared/ui'
import { useOnClickOutside } from 'usehooks-ts'

const budgetSchema = z.coerce.number().min(0, 'Budget must be at least 0')

const BUDGET_FORMAT = new Intl.NumberFormat('ru-RU')

export type BudgetCellProps = {
    budget?: number

    onChange: (v: number) => void

    //toggle mode
    edit: boolean
    onChangeEdit: (v: boolean) => void

    className?: string

    enabled: boolean
    disabledMessage?: string

    /** Increment to trigger shake animation. Non-zero = red border. */
    errorShakeKey?: number

    hasMarkingFormData: boolean
}
export const BudgetCell = ({
    budget: budgetProp = 0,
    onChange,
    edit,
    onChangeEdit,
    className,
    enabled,
    disabledMessage = '',
    errorShakeKey = 0,
    hasMarkingFormData,
}: BudgetCellProps) => {
    const { value: budget, setOptimisticValue } = useOptimisticValue(budgetProp)
    const [hasError, setHasError] = useState(false)

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        if (errorShakeKey > 0) setHasError(true)
    }, [errorShakeKey])

    const enableEdit = useCallback(() => {
        setHasError(false)
        onChangeEdit(true)
    }, [onChangeEdit])

    const onChangeValue = useCallback(
        (inputValue: string) => {
            const value = inputValue === '' ? 0 : inputValue
            const result = budgetSchema.safeParse(value)

            if (result.data === 0 && hasMarkingFormData) {
                setHasError(true)
                onChangeEdit(false)
                return
            }

            if (result.success) {
                setOptimisticValue(result.data)
                onChange(result.data)
                onChangeEdit(false)
            }
        },
        [hasMarkingFormData, setOptimisticValue, onChange, onChangeEdit]
    )

    return (
        <DisableTooltip enabled={!enabled} message={disabledMessage}>
            <div
                key={errorShakeKey}
                className={cl(
                    'editable',
                    'budget-cell',
                    'h-[30px] cursor-pointer',
                    hasError && 'budget-cell--error',
                    className
                )}
            >
                {edit ? (
                    <BudgetInput
                        value={budget.toString()}
                        onChangeValue={onChangeValue}
                    />
                ) : (
                    <div
                        className="flex h-full w-full items-center"
                        onClick={() => {
                            if (enabled) {
                                enableEdit()
                            }
                        }}
                    >
                        {budget === 0 ? (
                            <div className="long-dash grow">
                                <hr />
                            </div>
                        ) : (
                            <div className="grow">
                                {BUDGET_FORMAT.format(budget)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </DisableTooltip>
    )
}

type BudgetInputProps = {
    value: string
    onChangeValue: (value: string) => void
    onValidationError?: (err: z.ZodError) => void
}
const BudgetInput = ({
    value,
    onChangeValue,
    onValidationError,
}: BudgetInputProps) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [internalValue, setInternalValue] = useState(value)

    const onChange: React.ChangeEventHandler<HTMLInputElement> = (evt) => {
        const value = evt.target.value
        setInternalValue(value)

        if (onValidationError) {
            const result = budgetSchema.safeParse(value)
            if (!result.success) {
                onValidationError(result.error)
            }
        }
    }

    const onEnter = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        if (evt.key === 'Enter') {
            onChangeValue(internalValue)
        }
    }

    const onBlur = () => {
        onChangeValue(internalValue)
    }

    useOnClickOutside(inputRef, onBlur)

    return (
        <div className="budget-input-wrapper">
            <input
                ref={inputRef}
                type="number"
                value={internalValue}
                onChange={onChange}
                onBlur={onBlur}
                onKeyDown={onEnter}
                className="edit-input budget-cell__input"
            />
            <GreyRubleIconInline className="budget-cell__icon" />
        </div>
    )
}
